import { CURRENT_CLIENT } from '@/constants'

import type { GatingCopyInput, GatingQuotaState } from './types'

type PlansFeatureMeta = {
  label?: string
  unit?: string
  type?: string
}

type PlansConfig = {
  catalog?: Array<{ id: string; display_name?: string }>
  features?: Record<string, PlansFeatureMeta>
  default_plan_id?: string
}

export const FEATURED_JOB_FEATURE_KEY = 'job_board.feature'
export const PUBLISH_JOB_FEATURE_KEY = 'job_board.publish'
export const SMART_ANALYSIS_FEATURE_KEY = 'ai.smart_search_deep'
export const SEMANTIC_PROSPECTS_FEATURE_KEY = 'ai.semantic_prospects'

/** Gate popover / dialog secondary line for featured jobs. */
export const FEATURED_JOB_GATE_DESCRIPTION =
  'Get higher visibility on candidate browse and the dashboard Featured jobs section, with priority placement and a Featured badge.'

/** Form row help for the featured job checkbox. */
export const FEATURED_JOB_FORM_DESCRIPTION =
  'Priority placement in candidate browse and Featured jobs — starts when published.'

/** Gate popover / dialog secondary line for publishing jobs. */
export const PUBLISH_JOB_GATE_DESCRIPTION =
  'Publishing makes this role visible to candidates and ready for applications.'

/** Ordered benefit bullets shown in the Publish gate popover. */
export const PUBLISH_JOB_GATE_BENEFITS = [
  'Publish your jobs and start finding talent',
  'Rank every applicant automatically with AI-powered analysis',
  "Discover and invite talent who haven't applied yet",
  'Promote the role for priority placement and Featured badge',
]

/** Gate popover secondary line for semantic candidate suggestions. */
export const SEMANTIC_PROSPECTS_GATE_DESCRIPTION =
  'Shortlists people who match this role but have not applied yet.'

function plansConfig(): PlansConfig {
  const raw = (CURRENT_CLIENT as { plans?: PlansConfig }).plans
  return raw && typeof raw === 'object' ? raw : {}
}

/** Feature label from tenant client-config (falls back to key). */
export function featureLabelFromConfig(featureKey: string): string {
  const meta = plansConfig().features?.[featureKey]
  if (meta?.label?.trim()) return meta.label.trim()
  return featureKey
}

/** Unit label for quota features (e.g. "active_featured"). */
export function featureUnitFromConfig(featureKey: string): string | undefined {
  const unit = plansConfig().features?.[featureKey]?.unit
  return unit?.trim() || undefined
}

/** Display name for a plan id from catalog. */
export function planDisplayNameFromConfig(planId: string): string {
  const entry = plansConfig().catalog?.find((p) => p.id === planId)
  if (entry?.display_name?.trim()) return entry.display_name.trim()
  return planId
}

/** Plan name for user-facing gate copy — e.g. catalog "Growth" → "Growth Plan". */
export function planNameForCopy(planName: string): string {
  const trimmed = planName.trim()
  if (!trimmed) return 'Upgrade plan'
  if (/\bplan$/i.test(trimmed)) return trimmed
  return `${trimmed} Plan`
}

/** Headline-safe label — registry labels like "Featured job slots" read poorly in gate copy. */
export function gatingHeadlineFeatureLabel(
  featureKey: string | undefined,
  featureLabel: string,
): string {
  if (featureKey === FEATURED_JOB_FEATURE_KEY) return 'Featured jobs'
  if (featureKey === PUBLISH_JOB_FEATURE_KEY) return 'Publishing jobs'
  if (featureKey === SMART_ANALYSIS_FEATURE_KEY) return 'Analysis'
  if (featureKey === SEMANTIC_PROSPECTS_FEATURE_KEY) return 'Candidate invites'
  return featureLabel
}

/** Short title for dialogs/popovers (matches nearby form labels when possible). */
export function gatingSurfaceTitle(featureKey: string | undefined, featureLabel: string): string {
  if (featureKey === FEATURED_JOB_FEATURE_KEY) return 'Featured job'
  if (featureKey === PUBLISH_JOB_FEATURE_KEY) return 'Publish job'
  if (featureKey === SMART_ANALYSIS_FEATURE_KEY) return 'Analysis'
  if (featureKey === SEMANTIC_PROSPECTS_FEATURE_KEY) return 'Invite candidates'
  return featureLabel
}

/** Default gate description when callers omit `description`. */
export function gatingDescriptionForFeature(
  featureKey: string | undefined,
  description?: string,
): string | undefined {
  if (description?.trim()) return description.trim()
  if (featureKey === FEATURED_JOB_FEATURE_KEY) return FEATURED_JOB_GATE_DESCRIPTION
  if (featureKey === PUBLISH_JOB_FEATURE_KEY) return PUBLISH_JOB_GATE_DESCRIPTION
  if (featureKey === SEMANTIC_PROSPECTS_FEATURE_KEY) return SEMANTIC_PROSPECTS_GATE_DESCRIPTION
  return undefined
}

function formatQuotaUnit(unit: string | undefined, limit: number): string {
  if (unit === 'active_featured') {
    return limit === 1 ? 'featured job slot' : 'featured job slots'
  }
  if (unit?.trim()) return unit.trim()
  return limit === 1 ? 'slot' : 'slots'
}

function booleanAvailabilityHeadline(
  label: string,
  planLabel: string,
  featureKey?: string,
): string {
  if (featureKey === FEATURED_JOB_FEATURE_KEY) {
    return `${label} are available on ${planLabel}.`
  }
  if (featureKey === SEMANTIC_PROSPECTS_FEATURE_KEY) {
    return `${label} are available on ${planLabel}.`
  }
  if (featureKey === PUBLISH_JOB_FEATURE_KEY) {
    return `Upgrade to ${planLabel} to unlock Jobs Publishing.`
  }
  return `${label} is available on ${planLabel}.`
}

export type FeaturedJobQuotaFormDetails = {
  planLabel: string
  slotsLabel: string
  used: number
  limit: number
  exhausted: boolean
  exhaustionHint: string | null
}

/** Structured quota state for the featured job form row. */
export function featuredJobQuotaFormDetails(
  planName: string,
  quota: GatingQuotaState | undefined,
  blocked: boolean,
): FeaturedJobQuotaFormDetails | null {
  if (!quota || quota.limit <= 0) {
    return null
  }

  const slotsLabel = formatQuotaUnit(quota.unit, quota.limit)
  const used = Math.min(quota.used, quota.limit)
  const exhausted = blocked && quota.exhausted

  return {
    planLabel: planNameForCopy(planName),
    slotsLabel,
    used,
    limit: quota.limit,
    exhausted: quota.exhausted,
    exhaustionHint: exhausted
      ? `You've used all ${quota.limit} ${slotsLabel}. Unfeature another active job to free a slot.`
      : null,
  }
}

/** Quota suffix for the featured job form row (Growth usage / exhaustion). */
export function featuredJobQuotaFormSuffix(
  planName: string,
  quota: GatingQuotaState | undefined,
  blocked: boolean,
): string | null {
  const details = featuredJobQuotaFormDetails(planName, quota, blocked)
  if (!details) return null

  if (details.exhaustionHint) {
    return details.exhaustionHint
  }

  return `${details.planLabel} includes ${details.limit} ${details.slotsLabel}. ${details.used} of ${details.limit} currently used.`
}

/** Headline for boolean vs quota-exhausted gates. */
export function buildGatingHeadline(input: GatingCopyInput): string {
  const { planName, quota, featureKey } = input
  const planLabel = planNameForCopy(planName)
  const label = gatingHeadlineFeatureLabel(featureKey, input.featureLabel)
  if (quota?.exhausted && quota.limit > 0) {
    const unit = formatQuotaUnit(quota.unit, quota.limit)
    return `${planLabel} includes ${quota.limit} ${unit}. You've used all ${quota.used}.`
  }
  return booleanAvailabilityHeadline(label, planLabel, featureKey)
}

/** Secondary line under the headline. */
export function buildGatingDescription(input: GatingCopyInput): string {
  if (input.description?.trim()) return input.description.trim()
  if (input.quota?.exhausted && input.quota.limit > 0) {
    return 'Unfeature another active job to free a slot.'
  }
  const resolvedDescription = gatingDescriptionForFeature(input.featureKey, input.description)
  if (resolvedDescription) return resolvedDescription
  return `Upgrade to ${planNameForCopy(input.planName)} to unlock this for your workspace.`
}

/** Screen-reader label for locked controls. */
export function buildGatedAriaLabel(
  featureLabel: string,
  planName: string,
  featureKey?: string,
): string {
  const label = gatingHeadlineFeatureLabel(featureKey, featureLabel)
  return `${label}, locked, available on ${planNameForCopy(planName)}`
}
